/**
 * Het rechtermuismenu op het CANVAS zelf — niet op een object.
 *
 * Gedeeld door het React-Flow-canvas en het banen-canvas, om dezelfde reden
 * als `ConceptContextMenu`: een gebruiker die op het ene diagram een menu
 * krijgt en op het andere dat van de browser, leert er geen van beide.
 *
 * De opdrachten gaan over de WEERGAVE en over de selectie — niet over het
 * model, want op een lege plek is er geen object om iets mee te doen.
 */
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

export interface CanvasMenuStand {
  x: number;
  y: number;
}

export function CanvasContextMenu({
  stand,
  onClose,
  onAllesSelecteren,
  onInzoomen,
  onUitzoomen,
  onTerug,
  onPassend,
}: {
  stand: CanvasMenuStand;
  onClose: () => void;
  onAllesSelecteren?: () => void;
  onInzoomen: () => void;
  onUitzoomen: () => void;
  onTerug: () => void;
  onPassend: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const opKlik = (e: Event) => {
      if (e.target instanceof Node && ref.current?.contains(e.target)) return;
      onClose();
    };
    const opToets = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("mousedown", opKlik);
    window.addEventListener("pointerdown", opKlik, true);
    window.addEventListener("keydown", opToets);
    return () => {
      window.removeEventListener("mousedown", opKlik);
      window.removeEventListener("pointerdown", opKlik, true);
      window.removeEventListener("keydown", opToets);
    };
  }, [onClose]);

  const knop = "block w-full px-3 py-1 text-left text-(--ld-text) hover:bg-(--ld-raised)";
  const doe = (fn: () => void) => () => {
    fn();
    onClose();
  };

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: menu-oppervlak; de knoppen erin dragen de interactie
    <div
      ref={ref}
      className="ledger absolute z-20 min-w-[190px] rounded-md border border-(--ld-control) bg-(--ld-surface) py-1 text-sm shadow-xl ring-1 ring-(--ld-border)"
      style={{ left: stand.x, top: stand.y }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {onAllesSelecteren ? (
        <button type="button" className={knop} onClick={doe(onAllesSelecteren)}>
          {t("canvas.contextMenu.selectAll")}
        </button>
      ) : null}
      <button type="button" className={knop} onClick={doe(onPassend)}>
        {t("canvas.contextMenu.fitView")}
      </button>
      <button type="button" className={knop} onClick={doe(onInzoomen)}>
        {t("canvas.contextMenu.zoomIn")}
      </button>
      <button type="button" className={knop} onClick={doe(onUitzoomen)}>
        {t("canvas.contextMenu.zoomOut")}
      </button>
      <button type="button" className={knop} onClick={doe(onTerug)}>
        {t("canvas.contextMenu.zoomReset")}
      </button>
    </div>
  );
}
